import React, { Suspense, useCallback } from 'react';

import noop from 'lodash/noop';
import { ErrorBoundary } from 'react-error-boundary';
import { defineMessage, useIntl } from 'react-intl';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';

import type { DocNode } from '@atlaskit/adf-schema';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import { useFlags } from '@atlaskit/flag/flag-provider';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import Link from '@atlaskit/link';
import { rovoHexIcon } from '@atlaskit/logo/raw-icons';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { adfToMarkdownPlus } from '@atlassian/adf-markdown-transformer';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import fireErrorAnalytics from '@atlassian/error-handling/fire-error-analytics';
import { isRovoBroadcastConfirmedActions } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-broadcast-confirmed-action-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import { RovoBroadcastConfirmedActionsCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/rovo-broadcast-confirmed-action-command';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import type { AgenticPlanTool$key } from './__generated__/AgenticPlanTool.graphql';
import type { AgenticPlanToolInChatCompleteConversationMutation } from './__generated__/AgenticPlanToolInChatCompleteConversationMutation.graphql';
import type { AgenticPlanToolInChatQuery } from './__generated__/AgenticPlanToolInChatQuery.graphql';
import { AgenticPlanTool } from './AgenticPlanTool';
import { AgenticPlanWrapperInChat } from './AgenticPlanWrapperInChat';
import { CollapsibleCard } from './CollapsibleCard';
import { PACKAGE_NAME, TEAM_NAME } from './constants.tsx';
import { defaultFooter } from './DefaultWrappers.tsx';
import { SystemMessage } from './SystemMessage.tsx';
import type { InChatComponentsProps, PlanEditLevel } from './types.tsx';

const styles = cssMap({
	loadingHeaderOffset: {
		paddingBlockStart: token('space.050'),
		paddingBlockEnd: token('space.050'),
	},
});

const RovoHeaderIcon = () => (
	<Avatar
		size="small"
		name="Rovo"
		appearance="hexagon"
		src={rovoHexIcon}
		borderColor={token('elevation.surface')}
		testId="rovo-header-icon"
	/>
);

const subtitleMessage = defineMessage({
	id: 'conversation-assistant.agenticPlanTool.subtitle',
	defaultMessage: 'Plan for <link>{issueKey}</link>',
	description:
		'label for card subtitle in agentic plan tool, indicating the issue the agent is planning for',
});

const errorFallbackTitleMessage = defineMessage({
	id: 'conversation-assistant.agenticPlanTool.errorFallbackTitle',
	defaultMessage: 'Plan for {issueKey}',
	description:
		'Heading for the agentic plan card when issue data fails to load, showing the issue key as a fallback',
});

const planDismissedMessage = defineMessage({
	id: 'conversation-assistant.agenticPlanTool.planDismissed',
	defaultMessage: 'Plan dismissed',
	description: 'label for system message when an agentic plan is dismissed in chat',
});

const dismissButtonLabel = defineMessage({
	id: 'agentic-planning.in-chat.dismiss-button',
	defaultMessage: 'Dismiss',
	description: 'Button label to dismiss the proposed agentic plan in chat',
});

const planAcceptedMessage = defineMessage({
	id: 'conversation-assistant.agenticPlanTool.planAccepted',
	defaultMessage: 'Plan accepted',
	description: 'label for system message when an agentic plan is accepted in chat',
});

const completeConversationErrorTitle = defineMessage({
	id: 'agentic-planning.AgenticPlanToolInChat.completeConversationErrorTitle',
	defaultMessage: 'Something went wrong',
	description:
		'Title for the error flag shown when completing the agent session fails after publishing the plan from chat',
});

const completeConversationErrorContent = defineMessage({
	id: 'agentic-planning.AgenticPlanToolInChat.completeConversationErrorContent',
	defaultMessage: "We couldn't finish the agent session. Please try again.",
	description:
		'Content for the error flag shown when completing the agent session fails after publishing the plan from chat',
});

const getIssueKeyFromAction = (
	action: InChatComponentsProps['message']['actions'][number] | undefined,
): string => {
	const actionData = action?.data;
	const issueKeyFromData =
		typeof actionData === 'object' && actionData !== null && 'issueKey' in actionData
			? actionData.issueKey
			: undefined;

	if (typeof issueKeyFromData !== 'string' || !issueKeyFromData) {
		throw new Error(
			'AgenticPlanToolInChat: issueKey is required but was not found in message actions',
		);
	}

	return issueKeyFromData;
};

type InnerProps = InChatComponentsProps & {
	cloudId: string;
	isLoading?: boolean;
	issueRef?: AgenticPlanTool$key | null;
	messageId?: string;
	onCompleteConversation: (markdown: string, issueKey: string) => void;
};

const broadcastPlanPublished = () => {
	try {
		const bridgeAPI = getBrowserBridgeAPI();
		bridgeAPI.broadcast(
			RovoBroadcastConfirmedActionsCommand,
			{
				key: 'AgenticPlanTool',
				senderId: ROVO_CHAT_CLIENT_ID,
			},
			{ filter: isRovoBroadcastConfirmedActions },
		);
	} catch {
		// getBrowserBridgeAPI() throws if no bridge is mounted (e.g. in tests/SSR) — safe to ignore
	}
};

const AgenticPlanToolInner = ({
	agentName,
	message,
	onConfirm,
	handleOnDismiss,
	localActionState,
	markFinished,
	onCompleteConversation,
	isLastMessage,
	issueRef,
	isLoading,
}: InnerProps) => {
	const { formatMessage } = useIntl();

	const handleOnExecuteAgent = (
		currentPlan: DocNode,
		issueKey: string,
		savedToDescription: boolean = false,
		planEditLevel?: PlanEditLevel,
	) => {
		const markdown = adfToMarkdownPlus(currentPlan, { markdownPlus: false }).markdown;

		if (fg('jira_ai_task_level_planning_ootb_agent')) {
			// OOTB: publishing records the plan and completes the conversation
			// (onCompleteConversation), instead of resuming the agent via onConfirm.
			// The plan is already saved to the description by the tool.
			broadcastPlanPublished();
			onCompleteConversation(markdown, issueKey);
			markFinished();
			return;
		}

		const actions = [{ ...message.actions[0], data: { content: markdown, issueKey } }];
		if (savedToDescription) {
			broadcastPlanPublished();
		}
		onConfirm({ actions }, { savedToDescription, planEditLevel });
	};

	const footer = useCallback(
		(actionButtons: React.ReactNode) =>
			defaultFooter(
				<>
					{actionButtons}
					<Button appearance="subtle" onClick={handleOnDismiss}>
						{formatMessage(dismissButtonLabel)}
					</Button>
				</>,
			),
		[handleOnDismiss, formatMessage],
	);

	return (
		<AgenticPlanTool
			agentSessionInfo={{ agentName: agentName ?? '' }}
			isFromChat
			message={message}
			onExecuteAgent={handleOnExecuteAgent}
			onDismissSession={handleOnDismiss}
			onOpenRovoChat={noop}
			hideFooter={localActionState === 'finished' || isLastMessage === false}
			issueRef={issueRef}
			isLoadingIssue={isLoading}
			footer={footer}
		/>
	);
};

const AgenticPlanToolWithIssueData = ({ message, cloudId, ...props }: InnerProps) => {
	const { formatMessage } = useIntl();
	const issueKey = getIssueKeyFromAction(message.actions[0]);

	const data = useLazyLoadQuery<AgenticPlanToolInChatQuery>(
		graphql`
			query AgenticPlanToolInChatQuery($cloudId: ID!, $issueKey: String!) {
				jira {
					issueByKey(cloudId: $cloudId, key: $issueKey) {
						summary
						...AgenticPlanTool
						webUrl
					}
				}
			}
		`,
		{ cloudId, issueKey },
		{ fetchPolicy: 'store-or-network' },
	);

	const issue = data?.jira?.issueByKey;

	const subtitle = formatMessage(subtitleMessage, {
		issueKey,
		link: (chunks) =>
			issue?.webUrl ? (
				<Link href={issue.webUrl} appearance="subtle">
					{`${chunks}`}
				</Link>
			) : (
				chunks
			),
	});

	return (
		<>
			<CollapsibleCard
				defaultExpanded={props.localActionState === 'planned'}
				isExpandable
				title={issue?.summary ?? undefined}
				subtitle={subtitle}
				headerIcon={fg('jira_ai_task_level_planning_ootb_agent') ? undefined : <RovoHeaderIcon />}
			>
				<AgenticPlanToolInner cloudId={cloudId} message={message} issueRef={issue} {...props} />
			</CollapsibleCard>
			{props.localActionState === 'finished' && (
				<SystemMessage message={formatMessage(planAcceptedMessage)} />
			)}
		</>
	);
};

const AgenticPlanToolWithErrorFallback = (props: InnerProps) => {
	const { formatMessage } = useIntl();
	const issueKey = getIssueKeyFromAction(props.message.actions[0]);

	return (
		<CollapsibleCard
			defaultExpanded={props.localActionState === 'planned'}
			isExpandable
			title={formatMessage(errorFallbackTitleMessage, { issueKey })}
			headerIcon={fg('jira_ai_task_level_planning_ootb_agent') ? undefined : <RovoHeaderIcon />}
		>
			<AgenticPlanToolInner {...props} />
		</CollapsibleCard>
	);
};

const AgenticPlanToolWithHeaderLoading = (props: InnerProps) => {
	const skeletonIcon = (
		<Box xcss={styles.loadingHeaderOffset}>
			<Skeleton width="32px" height="32px" borderRadius={token('radius.small')} />
		</Box>
	);
	const skeletonSubtitle = (
		<Stack space="space.050" xcss={styles.loadingHeaderOffset}>
			<Skeleton width="180px" height="14px" borderRadius={token('radius.xsmall')} />
			<Skeleton width="100px" height="12px" borderRadius={token('radius.xsmall')} />
		</Stack>
	);

	return (
		<CollapsibleCard
			defaultExpanded={props.localActionState === 'planned'}
			isExpandable
			headerIcon={skeletonIcon}
			subtitle={skeletonSubtitle}
		>
			<AgenticPlanToolInner {...props} isLoading />
		</CollapsibleCard>
	);
};

type Props = ActionRendererFor<typeof ActionKeys.AgenticPlanTool>;

export const AgenticPlanToolInChat = (props: Props): React.JSX.Element => {
	const { actions, cloudId, conversationId, onCustomDataActioned } = props;
	const firstAction = actions[0];
	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();

	const [commitCompleteConversation] =
		useMutation<AgenticPlanToolInChatCompleteConversationMutation>(graphql`
			mutation AgenticPlanToolInChatCompleteConversationMutation(
				$cloudId: ID!
				$conversationId: ID!
			) {
				convoAi_completeConversation(cloudId: $cloudId, conversationId: $conversationId) {
					success
					errors {
						message
					}
				}
			}
		`);

	const showCompleteConversationErrorFlag = useCallback(() => {
		fireErrorAnalytics({
			meta: {
				id: 'agenticPlanToolInChatCompleteConversation',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			attributes: { conversationId },
		});
		showFlag({
			id: 'agentic-plan-tool-in-chat-complete-conversation-error',
			icon: <StatusErrorIcon color={token('color.icon.danger')} label="" />,
			title: formatMessage(completeConversationErrorTitle),
			description: formatMessage(completeConversationErrorContent),
		});
	}, [formatMessage, showFlag, conversationId]);

	const handleCompleteConversation = useCallback(
		(markdown: string, issueKey: string) => {
			if (!firstAction || !conversationId) {
				return;
			}
			// Record the published plan
			onCustomDataActioned(firstAction, { content: markdown, issueKey }, markdown);

			commitCompleteConversation({
				variables: { cloudId, conversationId },
				onCompleted: (response) => {
					const result = response.convoAi_completeConversation;
					if (!result?.success || (result.errors && result.errors.length > 0)) {
						showCompleteConversationErrorFlag();
					}
				},
				onError: showCompleteConversationErrorFlag,
			});
		},
		[
			commitCompleteConversation,
			cloudId,
			conversationId,
			firstAction,
			onCustomDataActioned,
			showCompleteConversationErrorFlag,
		],
	);

	return (
		<AgenticPlanWrapperInChat
			{...props}
			key={firstAction?.invocationId}
			interactionSource="agenticPlanToolRenderer"
		>
			{(wrapperProps) => {
				if (wrapperProps.localActionState === 'cancelled') {
					return (
						<SystemMessage message={formatMessage(planDismissedMessage)} isSuccessMessage={false} />
					);
				}

				return (
					<ErrorBoundary
						fallbackRender={() => (
							<AgenticPlanToolWithErrorFallback
								{...wrapperProps}
								cloudId={props.cloudId}
								onCompleteConversation={handleCompleteConversation}
							/>
						)}
					>
						<Suspense
							fallback={
								<AgenticPlanToolWithHeaderLoading
									{...wrapperProps}
									cloudId={props.cloudId}
									isLastMessage={props.isLastMessage}
									onCompleteConversation={handleCompleteConversation}
								/>
							}
						>
							<AgenticPlanToolWithIssueData
								{...wrapperProps}
								cloudId={props.cloudId}
								messageId={props.messageId}
								isLastMessage={props.isLastMessage}
								onCompleteConversation={handleCompleteConversation}
							/>
						</Suspense>
					</ErrorBoundary>
				);
			}}
		</AgenticPlanWrapperInChat>
	);
};
